import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, ZoomIn, ZoomOut } from "lucide-react";

const ZOOMS = [1, 1.5, 2, 3];

// Portaled to <body> so ancestor transforms (page entrance animations) cannot trap position:fixed.
// Full-screen viewer: image at full resolution, zoom steps + native scroll/pinch; closes on button, ESC or backdrop.
export function ImageLightbox({ src, alt, closeLabel, onClose }) {
  const [z, setZ] = useState(0);
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [onClose]);
  const btn = "w-10 h-10 rounded-full bg-black/70 border border-[#E5B842]/50 text-[#E5B842] flex items-center justify-center hover:bg-[#E5B842] hover:text-black disabled:opacity-30";
  // Aspect-agnostic: 100% = contain-fit in the viewport at the image's natural ratio; zoom scales that width.
  const [fitW, setFitW] = useState(0);
  const imgRef = useRef(null);
  const zoomTo = (next) => {
    if (z === 0 && imgRef.current) setFitW(imgRef.current.clientWidth);
    setZ(Math.max(0, Math.min(ZOOMS.length - 1, next)));
  };
  const fitStyle = { maxWidth: "calc(100vw - 32px)", maxHeight: "calc(100vh - 96px)", width: "auto", height: "auto" };
  const imgStyle = z === 0 || !fitW ? fitStyle : { width: `${Math.round(fitW * ZOOMS[z])}px`, height: "auto", flexShrink: 0 };
  return createPortal(
    <div role="dialog" aria-modal="true" aria-label={alt} data-testid="lightbox" className="fixed inset-0 z-[100] bg-black/95">
      <div data-testid="lightbox-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}
        className="absolute inset-0 overflow-auto flex p-4" style={{ touchAction: "pan-x pan-y pinch-zoom" }}>
        <img src={src} alt={alt} data-testid="lightbox-image" ref={imgRef} onClick={() => zoomTo((z + 1) % ZOOMS.length)}
          className={`m-auto select-none ${z === 0 ? "cursor-zoom-in" : "max-w-none cursor-zoom-in"}`} style={imgStyle} />
      </div>
      <div className="absolute top-4 right-4 flex gap-2">
        <button type="button" data-testid="lightbox-zoom-out" aria-label="Zoom out" className={btn} disabled={z === 0} onClick={() => zoomTo(z - 1)}><ZoomOut className="w-5 h-5" /></button>
        <button type="button" data-testid="lightbox-zoom-in" aria-label="Zoom in" className={btn} disabled={z === ZOOMS.length - 1} onClick={() => zoomTo(z + 1)}><ZoomIn className="w-5 h-5" /></button>
        <button type="button" data-testid="lightbox-close" aria-label={closeLabel} className={btn} onClick={onClose}><X className="w-5 h-5" /></button>
      </div>
      <div data-testid="lightbox-zoom-level" className="absolute bottom-4 left-1/2 -translate-x-1/2 text-xs font-mono text-[#E5B842] bg-black/70 px-3 py-1 rounded-full">{ZOOMS[z] * 100}%</div>
    </div>,
    document.body
  );
}
